// Registro firmado por el trabajador (M45): un solo modal para la asistencia a inducciones y capacitaciones, la
// entrega-recepción de EPP y el certificado de aptitud médica. Pasos: revisar el contenido mínimo → preparar para
// firma (contenido congelado y hash; el origen queda bloqueado) → firmar en pantalla (con el dedo, a 360 px) o
// imprimir el formato y subir el escaneo firmado → cerrar. Firmado, solo el administrador lo reabre con un motivo
// (queda en el historial de cambios). Reutiliza SignaturePad (permisos de trabajo, plan e inspecciones).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignaturePad, { type SignaturePadHandle } from '../permits/SignaturePad';
import {
  ACEPTA_ADJUNTO, cerrarRegistro, dataUrlFirma, firmar, prepararRegistro, reabrirRegistro, subirAdjunto, urlArchivo, volverAEditar,
  type OrigenRegistro,
} from '../../lib/firmas/firmas-datos';
import {
  cedulaValida, claveFirma, estadoVisible, faltantesParaFirmar, firmaDe, firmaSinTrabajador, firmasRequeridas, puedeVolverAEditar,
  registroSinOrigen, registroSinTrabajador, requiereReapertura, validarMotivo, type FirmaRequerida,
} from '../../lib/firmas/firmas-logica';
import { fechaHora, generarRegistroPDF, nombreArchivoRegistro, tituloRegistro } from '../../lib/firmas/firmas-pdf';
import type { BucketRegistro, Contenido, FilaFirma, FilaRegistro, MetodoRegistro } from '../../lib/firmas/firmas-tipos';
import {
  ETIQUETA_APTITUD, ETIQUETA_CLASE, ETIQUETA_EVALUACION, ETIQUETA_ROL, ETIQUETA_TIPO_EXAMEN, MARCA_TRABAJADOR_ELIMINADO, TEXTO_FIRMA_SIMPLE,
} from '../../lib/firmas/firmas-tipos';
import { plural } from '../../lib/plurales';

export interface RegistroFirmasProps {
  titulo: string;
  companyId: string;
  usuarioId: string | null;
  registro: FilaRegistro | null;
  firmas: FilaFirma[];
  origen: OrigenRegistro;
  /** Contenido actual del origen (para preparar) y lo que le falta según el Anexo 1. */
  armarContenido: () => Promise<{ contenido: Contenido; faltantes: string[] }>;
  devoluciones?: Record<string, string | null>;
  puedeEditar: boolean;   // admin o manager
  esAdmin: boolean;       // reabre
  onCambio: () => void;
  onCerrar: () => void;
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '12px 8px', overflowY: 'auto' } as React.CSSProperties,
  caja: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, width: '100%', maxWidth: 680, boxSizing: 'border-box', margin: 'auto 0' } as React.CSSProperties,
  cabecera: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, padding: '16px 16px 10px', borderBottom: '1px solid #334155' } as React.CSSProperties,
  cuerpo: { padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 } as React.CSSProperties,
  bloque: { background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 } as React.CSSProperties,
  h3: { color: '#e2e8f0', fontSize: '0.92rem', fontWeight: 700, margin: 0 } as React.CSSProperties,
  nota: { color: '#94a3b8', fontSize: '0.78rem', margin: 0, lineHeight: 1.45 } as React.CSSProperties,
  fila: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid rgba(51,65,85,0.6)' } as React.CSSProperties,
  botones: { display: 'flex', gap: 8, flexWrap: 'wrap' } as React.CSSProperties,
  input: { width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', padding: '9px 10px', fontSize: '0.88rem' } as React.CSSProperties,
};
const boton = (tipo: 'primario' | 'secundario' | 'peligro' = 'secundario', deshabilitado = false): React.CSSProperties => ({
  padding: '9px 14px', borderRadius: 8, fontWeight: 600, fontSize: '0.84rem', cursor: deshabilitado ? 'not-allowed' : 'pointer', opacity: deshabilitado ? 0.55 : 1,
  minHeight: 40,
  ...(tipo === 'primario' ? { background: '#2FBF71', border: '1px solid #2FBF71', color: '#0f172a' }
    : tipo === 'peligro' ? { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#f87171' }
      : { background: 'transparent', border: '1px solid #475569', color: '#cbd5e1' }),
});
const COLOR_ESTADO: Record<string, string> = { sin_registro: '#94a3b8', abierto: '#94a3b8', en_firma: '#f59e0b', firmado: '#2FBF71' };

function abrirConUrl(promesa: Promise<string>) {
  // La pestaña se abre en el mismo clic (Safari bloquea window.open después de una espera).
  const v = window.open('', '_blank');
  if (v) v.opener = null;
  promesa.then(url => { if (v) v.location.href = url; else window.open(url, '_blank', 'noopener,noreferrer'); })
    .catch(e => { v?.close(); alert(msg(e)); });
}

export default function RegistroFirmasModal(p: RegistroFirmasProps) {
  const [reg, setReg] = useState<FilaRegistro | null>(p.registro);
  const [firmas, setFirmas] = useState<FilaFirma[]>(p.firmas);
  const [vista, setVista] = useState<{ contenido: Contenido; faltantes: string[] } | null>(null);
  const [metodo, setMetodo] = useState<MetodoRegistro>(p.registro?.sign_method ?? 'pantalla');
  const [firmando, setFirmando] = useState<FirmaRequerida | null>(null);
  const [nombre, setNombre] = useState('');
  const [cedula, setCedula] = useState('');
  const [conTrazo, setConTrazo] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [reabrir, setReabrir] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const pad = useRef<SignaturePadHandle>(null);
  const cambio = useRef(false);

  const contenido: Contenido | null = reg && reg.status !== 'abierto' && reg.snapshot ? reg.snapshot : vista?.contenido ?? null;
  const estado = estadoVisible(reg, firmas);
  const bloqueadoConFirmas = requiereReapertura(reg, firmas.length);

  const armar = useRef(p.armarContenido);
  armar.current = p.armarContenido;
  const cargarVista = useCallback(async () => {
    try { setVista(await armar.current()); } catch (e) { setError(msg(e)); }
  }, []);
  const abierto = !reg || reg.status === 'abierto';
  useEffect(() => { if (abierto) cargarVista(); }, [abierto, cargarVista]);

  const ejecutar = async (fn: () => Promise<void>, exito?: string) => {
    setOcupado(true); setError(''); setOk('');
    try { await fn(); cambio.current = true; if (exito) setOk(exito); } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  };
  const cerrarModal = () => { if (ocupado) return; if (cambio.current) p.onCambio(); p.onCerrar(); };

  const requeridas = useMemo(() => (contenido && reg?.status !== 'abierto' ? firmasRequeridas(contenido) : []), [contenido, reg?.status]);
  const faltanCerrar = reg ? faltantesParaFirmar(reg, firmas, metodo) : [];

  // ── Acciones ──
  const preparar = () => ejecutar(async () => {
    const v = await armar.current();
    setVista(v);
    if (v.faltantes.length) throw new Error('Completa los datos que faltan antes de preparar el registro.');
    setReg(await prepararRegistro(p.companyId, reg, v.contenido, p.origen, p.usuarioId));
  }, 'Registro preparado: el contenido quedó congelado. Ya se puede firmar.');

  const editar = () => ejecutar(async () => { setReg(await volverAEditar(reg!)); setFirmas([]); }, 'Puedes volver a editar los datos.');

  const empezarFirma = (r: FirmaRequerida) => {
    setFirmando(r); setConTrazo(false); setError(''); setOk('');
    setNombre(r.rol === 'trabajador' ? r.nombre : r.nombre || '');
    setCedula('');
  };
  const cedulaDe = (r: FirmaRequerida): string | null => {
    if (!contenido || r.rol !== 'trabajador') return cedula.trim() || null;
    if (contenido.tipo === 'asistencia' || contenido.tipo === 'socializacion') return contenido.asistentes.find(a => a.worker_id === r.worker_id)?.cedula ?? null;
    return contenido.cedula;
  };
  const guardarFirma = () => ejecutar(async () => {
    if (!reg || !firmando) return;
    if (firmando.rol !== 'trabajador') {
      if (nombre.trim().length < 3) throw new Error('Escribe el nombre de quien firma.');
      if (cedula.trim() && !cedulaValida(cedula)) throw new Error('La cédula debe tener 10 dígitos.');
    }
    const png = await pad.current?.toBlob();
    if (!png) throw new Error('Dibuja la firma en el recuadro.');
    const f = await firmar(reg, { rol: firmando.rol, worker_id: firmando.worker_id, nombre: nombre.trim(), cedula: cedulaDe(firmando) }, png);
    setFirmas(x => [...x, f]);
    setFirmando(null);
  }, 'Firma guardada con la fecha y hora del servidor.');

  const adjuntar = (para: 'escaneo' | 'certificado') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    e.target.value = '';
    if (!archivo || !reg) return;
    ejecutar(async () => setReg(await subirAdjunto(reg, archivo, para)), para === 'escaneo' ? 'Escaneo subido.' : 'Certificado del médico subido.');
  };

  const cerrar = () => ejecutar(async () => { setReg(await cerrarRegistro(reg!, metodo)); }, 'Registro firmado y cerrado: queda bloqueado.');

  const reabrirUI = () => ejecutar(async () => {
    const e = validarMotivo(motivo);
    if (e) throw new Error(e);
    await reabrirRegistro(reg!.id, motivo);
    setReg({ ...reg!, status: 'abierto', signed_at: null, sign_method: null, scan_path: null, scan_bucket: null, doctor_cert_path: null,
      doctor_cert_bucket: null, reopen_history: [...reg!.reopen_history, { motivo: motivo.trim() } as FilaRegistro['reopen_history'][number]] });
    setFirmas([]); setReabrir(false); setMotivo('');
  }, 'Registro reabierto. El motivo quedó en el historial de cambios.');

  const pdf = (blanco: boolean) => ejecutar(async () => {
    if (!contenido) return;
    const conImagen = blanco ? [] : await Promise.all(firmas.map(async f => ({
      rol: f.role, worker_id: f.worker_id, nombre: f.signer_name, cedula: f.signer_cedula, firmadoEn: f.signed_at, hash: f.content_hash,
      imagen: await dataUrlFirma(f.image_path),
    })));
    const doc = generarRegistroPDF({
      contenido, hash: reg?.content_hash ?? null, estado: reg?.status ?? 'sin_registro', metodo: reg?.sign_method ?? null,
      firmadoEn: reg?.signed_at ?? null, firmas: conImagen, devoluciones: p.devoluciones, certificadoMedico: !!reg?.doctor_cert_path,
      reaperturas: reg?.reopen_history.length ?? 0, trabajadorEliminado: registroSinTrabajador(reg), blanco, fecha: new Date(),
    });
    doc.save(nombreArchivoRegistro(contenido, blanco));
  });

  const verArchivo = (bucket: BucketRegistro | null, ruta: string | null) => { if (bucket && ruta) abrirConUrl(urlArchivo(bucket, ruta)); };

  // ── Resumen del contenido ──
  const sinTrabajador = registroSinTrabajador(reg);
  const marca = (nombre: string, eliminado: boolean) => (eliminado ? `${nombre} ${MARCA_TRABAJADOR_ELIMINADO}` : nombre);
  const resumen = (c: Contenido): [string, string][] => {
    if (c.tipo === 'socializacion') return [['Procedimiento', c.titulo], ['Fecha', c.fecha.split('-').reverse().join('/')],
      ['Socializado por', c.tecnico || '—'], ['Asistentes', String(c.asistentes.length)]];
    if (c.tipo === 'asistencia') return [['Tipo', ETIQUETA_CLASE[c.clase]], ['Fecha', c.fecha.split('-').reverse().join('/')], ['Tema', c.tema || c.titulo],
      ['Técnico', c.tecnico || '—'], ['Material', c.material || '—'], ['Evaluación', c.evaluacion || '—'], ['Asistentes', String(c.asistentes.length)]];
    if (c.tipo === 'epp_entrega') return [['Trabajador', marca(c.trabajador, sinTrabajador)], ['Cédula', c.cedula || '—'], ['Fecha', c.fecha.split('-').reverse().join('/')],
      ['EPP', c.items.map(i => i.epp).join(', ') || '—']];
    return [['Trabajador', marca(c.trabajador, sinTrabajador)], ['Cédula', c.cedula || '—'], ['Fecha', c.fecha.split('-').reverse().join('/')],
      ['Examen', ETIQUETA_TIPO_EXAMEN[c.tipo_examen] ?? (c.tipo_examen || '—')], ['Aptitud', ETIQUETA_APTITUD[c.aptitud] ?? (c.aptitud || '—')], ['Médico', c.medico || '—']];
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && cerrarModal()} role="dialog" aria-modal="true" aria-label={p.titulo}>
      <div style={S.caja}>
        <div style={S.cabecera}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 700, margin: 0 }}>{contenido ? tituloRegistro(contenido) : 'Registro firmado'}</h2>
            <p style={{ ...S.nota, marginTop: 4, overflowWrap: 'anywhere' }}>{p.titulo}</p>
            <span style={{ display: 'inline-block', marginTop: 6, fontSize: '0.75rem', fontWeight: 700, color: COLOR_ESTADO[estado.estado] }}>{estado.texto}</span>
          </div>
          <button type="button" onClick={cerrarModal} aria-label="Cerrar" style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.2rem', cursor: 'pointer', minWidth: 40, minHeight: 40 }}>✕</button>
        </div>

        <div style={S.cuerpo}>
          {error && <p role="alert" style={{ ...S.nota, color: '#f87171' }}>{error}</p>}
          {ok && <p role="status" style={{ ...S.nota, color: '#4ade80' }}>{ok}</p>}

          {contenido && (
            <div style={S.bloque}>
              <h3 style={S.h3}>Contenido del registro</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(80px, auto) 1fr', gap: '4px 10px', fontSize: '0.82rem' }}>
                {resumen(contenido).map(([k, v]) => (
                  <React.Fragment key={k}><span style={{ color: '#64748b' }}>{k}</span><span style={{ color: '#e2e8f0', overflowWrap: 'anywhere' }}>{v}</span></React.Fragment>
                ))}
              </div>
              {reg?.content_hash && reg.status !== 'abierto' && <p style={{ ...S.nota, fontSize: '0.7rem', overflowWrap: 'anywhere' }}>Huella del contenido (SHA-256): {reg.content_hash}</p>}
            </div>
          )}

          {reg && registroSinOrigen(reg) && (
            <p style={{ ...S.nota, color: '#fbbf24' }}>
              El origen de este registro ({reg.kind === 'asistencia' ? 'la capacitación' : reg.kind === 'aptitud_medica' && reg.worker_id ? 'el examen' : 'el trabajador'}) se borró.
              El registro y sus firmas se conservan como evidencia, con el nombre y la cédula del contenido firmado.
            </p>
          )}

          {reg && reg.reopen_history.length > 0 && (
            <p style={S.nota}>Reabierto {reg.reopen_history.length === 1 ? 'una vez' : `${reg.reopen_history.length} veces`}. Último motivo: {reg.reopen_history[reg.reopen_history.length - 1].motivo}</p>
          )}

          {/* 1. Sin registro o abierto: revisar y preparar */}
          {(!reg || reg.status === 'abierto') && (
            <div style={S.bloque}>
              <h3 style={S.h3}>Preparar para firma</h3>
              <p style={S.nota}>Al preparar, el contenido queda congelado con su huella SHA-256 y los datos de origen ya no se pueden editar mientras se firma.</p>
              {vista && vista.faltantes.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18, color: '#fbbf24', fontSize: '0.8rem' }}>{vista.faltantes.map(f => <li key={f}>{f}</li>)}</ul>
              )}
              {p.puedeEditar ? (
                <div style={S.botones}>
                  <button type="button" style={boton('primario', ocupado || !vista || vista.faltantes.length > 0)} disabled={ocupado || !vista || vista.faltantes.length > 0} onClick={preparar}>Preparar para firma</button>
                  <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={cargarVista}>Volver a revisar</button>
                </div>
              ) : <p style={S.nota}>Solo el administrador o el manager preparan y recogen las firmas.</p>}
            </div>
          )}

          {/* 2. En firma */}
          {reg && reg.status === 'en_firma' && contenido && (
            <div style={S.bloque}>
              <h3 style={S.h3}>Firmas</h3>
              <div role="tablist" style={S.botones}>
                {(['pantalla', 'papel'] as MetodoRegistro[]).map(m => (
                  <button key={m} type="button" role="tab" aria-selected={metodo === m} onClick={() => setMetodo(m)}
                    style={{ ...boton(metodo === m ? 'primario' : 'secundario'), flex: '1 1 140px' }}>
                    {m === 'pantalla' ? 'Firmar en pantalla' : 'Formato impreso'}
                  </button>
                ))}
              </div>

              {metodo === 'pantalla' ? (
                <>
                  <p style={S.nota}>Cada persona firma con el dedo o el mouse en esta pantalla. El nombre y la cédula del trabajador salen de Trabajadores; la fecha y la hora, del servidor.</p>
                  {requeridas.map(r => {
                    const f = firmaDe(firmas, r);
                    const etiqueta = r.rol === 'trabajador' ? marca(r.nombre, (!!f && firmaSinTrabajador(f)) || sinTrabajador)
                      : `${ETIQUETA_ROL[r.rol]}${r.obligatoria ? '' : ' (opcional)'}`;
                    const evaluacion = (contenido.tipo === 'asistencia' || contenido.tipo === 'socializacion') && r.rol === 'trabajador'
                      ? contenido.asistentes.find(a => a.worker_id === r.worker_id)?.evaluacion : null;
                    return (
                      <div key={claveFirma(r.rol, r.worker_id)} style={S.fila}>
                        <div style={{ minWidth: 0, flex: '1 1 180px' }}>
                          <div style={{ color: '#e2e8f0', fontSize: '0.86rem', fontWeight: 600, overflowWrap: 'anywhere' }}>{etiqueta}</div>
                          <div style={{ color: '#64748b', fontSize: '0.74rem' }}>
                            {f ? `${f.signer_name}${f.signer_cedula ? ` · C.I. ${f.signer_cedula}` : ''} · ${fechaHora(f.signed_at)}`
                              : r.rol === 'trabajador' ? `C.I. ${cedulaDe(r) || '—'}${evaluacion ? ` · ${ETIQUETA_EVALUACION[evaluacion]}` : ''}` : ETIQUETA_ROL[r.rol]}
                          </div>
                        </div>
                        {f ? <span style={{ color: '#2FBF71', fontSize: '0.8rem', fontWeight: 700 }}>✓ Firmado</span>
                          : p.puedeEditar && <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => empezarFirma(r)}>Firmar</button>}
                      </div>
                    );
                  })}

                  {firmando && (
                    <div style={{ ...S.bloque, borderColor: '#2FBF71' }}>
                      <h3 style={S.h3}>Firma de {firmando.rol === 'trabajador' ? firmando.nombre : ETIQUETA_ROL[firmando.rol].toLowerCase()}</h3>
                      {firmando.rol !== 'trabajador' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                          <label style={S.nota}>Nombre<input style={S.input} value={nombre} onChange={e => setNombre(e.target.value)} disabled={ocupado} maxLength={160} /></label>
                          <label style={S.nota}>Cédula (opcional)<input style={S.input} value={cedula} inputMode="numeric" onChange={e => setCedula(e.target.value.replace(/\D/g, '').slice(0, 10))} disabled={ocupado} /></label>
                        </div>
                      )}
                      <SignaturePad ref={pad} height={170} disabled={ocupado} onChange={vacio => setConTrazo(!vacio)} />
                      <p style={{ ...S.nota, fontSize: '0.7rem' }}>{TEXTO_FIRMA_SIMPLE}</p>
                      <div style={S.botones}>
                        <button type="button" style={boton('primario', ocupado || !conTrazo)} disabled={ocupado || !conTrazo} onClick={guardarFirma}>Guardar firma</button>
                        <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => setFirmando(null)}>Cancelar</button>
                      </div>
                    </div>
                  )}

                  {contenido.tipo === 'aptitud_medica' && (
                    <div style={S.fila}>
                      <div style={{ minWidth: 0, flex: '1 1 180px' }}>
                        <div style={{ color: '#e2e8f0', fontSize: '0.86rem', fontWeight: 600 }}>Certificado firmado por el médico</div>
                        <div style={{ color: '#64748b', fontSize: '0.74rem' }}>PDF con firma electrónica, o foto o escaneo firmado a mano.</div>
                      </div>
                      {reg.doctor_cert_path ? (
                        <button type="button" style={boton('secundario')} onClick={() => verArchivo(reg.doctor_cert_bucket, reg.doctor_cert_path)}>Ver certificado</button>
                      ) : p.puedeEditar && (
                        <label style={boton('secundario', ocupado)}>Subir certificado
                          <input type="file" accept={ACEPTA_ADJUNTO} onChange={adjuntar('certificado')} disabled={ocupado} style={{ display: 'none' }} />
                        </label>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p style={S.nota}>Descarga el formato, imprímelo, recoge las firmas a mano y sube el escaneo o una foto legible. El formato lleva la huella del contenido.</p>
                  <div style={S.botones}>
                    <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => pdf(true)}>Imprimir para firma manual</button>
                    {reg.scan_path ? (
                      <button type="button" style={boton('secundario')} onClick={() => verArchivo(reg.scan_bucket, reg.scan_path)}>Ver escaneo</button>
                    ) : p.puedeEditar && (
                      <label style={boton('secundario', ocupado)}>Subir escaneo firmado
                        <input type="file" accept={ACEPTA_ADJUNTO} onChange={adjuntar('escaneo')} disabled={ocupado} style={{ display: 'none' }} />
                      </label>
                    )}
                  </div>
                </>
              )}

              {faltanCerrar.length > 0 && <ul style={{ margin: 0, paddingLeft: 18, color: '#fbbf24', fontSize: '0.8rem' }}>{faltanCerrar.map(f => <li key={f}>{f}</li>)}</ul>}
              {p.puedeEditar && (
                <div style={S.botones}>
                  <button type="button" style={boton('primario', ocupado || faltanCerrar.length > 0)} disabled={ocupado || faltanCerrar.length > 0} onClick={cerrar}>Cerrar registro firmado</button>
                  {puedeVolverAEditar(reg, firmas.length) && (
                    <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={editar}>Volver a editar los datos</button>
                  )}
                </div>
              )}
              {firmas.length > 0 && <p style={S.nota}>{plural(firmas.length, 'firma guardada', 'firmas guardadas')}: el contenido ya no cambia. Para corregirlo, el administrador reabre el registro con un motivo.</p>}
            </div>
          )}

          {/* 3. Firmado */}
          {reg && reg.status === 'firmado' && (
            <div style={S.bloque}>
              <h3 style={S.h3}>Registro firmado</h3>
              {firmas.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18, color: '#cbd5e1', fontSize: '0.8rem' }}>
                  {firmas.map(f => (
                    <li key={f.id} style={{ overflowWrap: 'anywhere' }}>
                      {f.role === 'trabajador' ? marca(f.signer_name, firmaSinTrabajador(f)) : `${ETIQUETA_ROL[f.role]}: ${f.signer_name}`}
                      {f.signer_cedula ? ` · C.I. ${f.signer_cedula}` : ''} · {fechaHora(f.signed_at)}
                    </li>
                  ))}
                </ul>
              )}
              <p style={S.nota}>Cerrado el {fechaHora(reg.signed_at)} ({reg.sign_method === 'papel' ? 'formato impreso firmado' : 'firma en pantalla'}). Queda bloqueado: solo el administrador puede reabrirlo con un motivo.</p>
              <div style={S.botones}>
                {reg.scan_path && <button type="button" style={boton('secundario')} onClick={() => verArchivo(reg.scan_bucket, reg.scan_path)}>Ver escaneo</button>}
                {reg.doctor_cert_path && <button type="button" style={boton('secundario')} onClick={() => verArchivo(reg.doctor_cert_bucket, reg.doctor_cert_path)}>Ver certificado del médico</button>}
              </div>
            </div>
          )}

          {contenido && reg && reg.status !== 'abierto' && (
            <div style={S.botones}>
              <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => pdf(false)}>Descargar PDF</button>
            </div>
          )}

          {/* Reabrir (solo admin) */}
          {bloqueadoConFirmas && p.esAdmin && (
            <div style={S.bloque}>
              {!reabrir ? (
                <button type="button" style={boton('peligro', ocupado)} disabled={ocupado} onClick={() => setReabrir(true)}>Reabrir registro</button>
              ) : (
                <>
                  <label style={S.nota}>Motivo para reabrir (queda en el historial de cambios)
                    <textarea style={{ ...S.input, minHeight: 70 }} value={motivo} onChange={e => setMotivo(e.target.value)} disabled={ocupado} maxLength={500} />
                  </label>
                  <p style={{ ...S.nota, fontSize: '0.72rem' }}>Las firmas guardadas pasan al historial del registro y hay que volver a firmar.</p>
                  <div style={S.botones}>
                    <button type="button" style={boton('peligro', ocupado || motivo.trim().length < 10)} disabled={ocupado || motivo.trim().length < 10} onClick={reabrirUI}>Reabrir</button>
                    <button type="button" style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => { setReabrir(false); setMotivo(''); }}>Cancelar</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
